import { createRequestFn, type RequestContext, type SDKError, type SDKResult } from '@saas/api-client';
import { getGlobalStoreCookie, type GlobalStore } from '@/stores/global.store';
import { handleContractHeader } from './api-contract';

export const COMPANY_ID_HEADER = 'x-company-id';

const globalStoreBridge = {
	getToken: (): string | null => null,
	setToken: (_token: string) => {},
	onLogout: () => {},
	getLanguage: (): string => 'en',
	getCompanyId: (): string | null => null,
};

export function connectStoreBridge(store: GlobalStore) {
	globalStoreBridge.getToken = () => store.getState().token;
	globalStoreBridge.setToken = (token: string) => store.setState((p) => ({ ...p, token }));
	globalStoreBridge.onLogout = () => store.onLogout();
	globalStoreBridge.getLanguage = () => store.getState().language;
	globalStoreBridge.getCompanyId = () =>
		store.getState().currentCompany?.id ?? getGlobalStoreCookie()?.selectedCompanyID ?? null;
}

export const getRequestLanguage = () => globalStoreBridge.getLanguage();

const setCompanyHeader = (headers: Headers): void => {
	if (headers.get(COMPANY_ID_HEADER)) return;
	const companyId = globalStoreBridge.getCompanyId();
	if (companyId) headers.set(COMPANY_ID_HEADER, companyId);
};

type CompanyHeaderInput = { companyId: string };

export type CompanyHeaderOptions = { headers: Record<string, string> };

export const withCompanyHeader = ({ companyId }: CompanyHeaderInput): Partial<CompanyHeaderOptions> => {
	if (!companyId) return {};
	return { headers: { [COMPANY_ID_HEADER]: companyId } };
};

/**
 * Current auth bearer token, or null when unauthenticated. Exposed so the
 * better-auth `authClient` (which lives outside the SDK request pipeline) can
 * attach `Authorization: Bearer` to its protected calls (e.g. change-password).
 */
export const getAuthToken = (): string | null => globalStoreBridge.getToken();

export type ApiErrorNotice = {
	method: string;
	routeId: string;
	status: number;
	errorCode?: string;
};

type ApiErrorObserver = (notice: ApiErrorNotice) => void;

let apiErrorObserver: ApiErrorObserver | null = null;

export const observeApiErrors = (observer: ApiErrorObserver): (() => void) => {
	apiErrorObserver = observer;
	return () => {
		if (apiErrorObserver === observer) apiErrorObserver = null;
	};
};

const readErrorCode = (body: unknown): string | undefined => {
	if (body === null || typeof body !== 'object' || !('code' in body)) return undefined;
	return typeof body.code === 'string' ? body.code : undefined;
};

const notifyApiError = (error: SDKError, context: RequestContext): void => {
	if (!apiErrorObserver) return;
	try {
		apiErrorObserver({
			method: context.method,
			routeId: context.path,
			status: error.status,
			errorCode: readErrorCode(error.body),
		});
	} catch {
		return;
	}
};

async function getValidToken(): Promise<string> {
	const token = globalStoreBridge.getToken();

	if (!token) {
		throw new Error('Not authenticated');
	}

	// better-auth sessions are validated server-side; no client-side JWT expiry check needed.
	// If a 401 is returned, the onResponse handler will trigger logout.
	return token;
}

export const globalAuthRequestFunction = createRequestFn({
	baseUrl: import.meta.env.VITE_API_URL,
	async onResponse(response) {
		handleContractHeader({ response });
		if (response.status === 401) {
			globalStoreBridge.onLogout();
		}
	},
	onError: notifyApiError,
	async onRequest(_url, init) {
		const headers = new Headers(init.headers);
		headers.set('Accept-Language', globalStoreBridge.getLanguage());
		headers.set('x-vadio-client', 'web');
		setCompanyHeader(headers);

		try {
			const token = await getValidToken();
			headers.set('Authorization', `Bearer ${token}`);
		} catch (error) {
			console.error(error);
			globalStoreBridge.onLogout();
			return init;
		}

		return { ...init, headers };
	},
});

export type ApiFetchInput = {
	path: string;
	init?: RequestInit;
};

export async function authFetch({ path, init = {} }: ApiFetchInput): Promise<Response> {
	const token = await getValidToken();
	const headers = new Headers(init.headers);
	headers.set('Authorization', `Bearer ${token}`);
	headers.set('Accept-Language', globalStoreBridge.getLanguage());
	setCompanyHeader(headers);

	const response = await fetch(`${import.meta.env.VITE_API_URL}${path}`, { ...init, headers });
	handleContractHeader({ response });
	if (response.status === 401) globalStoreBridge.onLogout();
	return response;
}

export async function publicFetch({ path, init = {} }: ApiFetchInput): Promise<Response> {
	const headers = new Headers(init.headers);
	headers.set('Accept-Language', globalStoreBridge.getLanguage());
	const response = await fetch(`${import.meta.env.VITE_API_URL}${path}`, { ...init, headers });
	handleContractHeader({ response });
	return response;
}

export const publicRequestFunction = createRequestFn({
	baseUrl: import.meta.env.VITE_API_URL,
	async onResponse(response) {
		handleContractHeader({ response });
	},
	onError: notifyApiError,
	async onRequest(_url, init) {
		const headers = new Headers(init.headers);
		headers.set('Accept-Language', globalStoreBridge.getLanguage());
		return { ...init, headers };
	},
});

export const withUnwrapSDKResult = <TArgs extends unknown[], TData>(
	fn: (...args: TArgs) => Promise<SDKResult<TData>>,
): ((...args: TArgs) => Promise<TData>) => {
	return (...args: TArgs) => {
		return fn(...args).then((res) => {
			if (res.error) throw res.error;
			return res.data;
		}) as Promise<TData>;
	};
};
